import { CommonObject } from '@/utils/types';
import { ErrorAlertModule } from '@/store/modules/errors';
import {ErrorMessage, OBLIGATION_TYPES, SubjectType, SubjectTypeValues} from '@/constants';
import { Role, RoleValues } from '@/utils/dictionaries';
import { FieldGroup } from '@/utils/fullStatementView';
import { AddStatementDraftResp } from '@/api/additionalStatement';
import { StatementType } from '@/types/services';
import { AdditionalDocumentsType } from '@/types/alternativeServices';
import router, { RoutePath, RouteText } from '@/router';
import { IBreadCrumbs } from '@/store/modules/breadCrumbs';
import { notaryTariffDate } from '@/app.config';

type GetSubDescriptionPayload = {
  name?: string;
  docDate?: string;
  number?: string;
};

type ApiError = { errorString: string } | string;

export function getSubDescription({
  name,
  docDate,
  number,
}: GetSubDescriptionPayload) {
  if (name || docDate || number) {
    return [name || '', docDate || '', number || ''].join(' ');
  }

  return '';
}

export function emptyToNull(obj: CommonObject) {
  for (const prop in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, prop)) {
      const fieldValue = obj[prop];

      if (typeof fieldValue === 'object') {
        emptyToNull(fieldValue);
      } else if (fieldValue === '') {
        obj[prop] = null;
      }
    }
  }

  return obj;
}

export function converterFromPennyToRub(
  value: any,
  toFixed = 2,
  separator = ','
) {
  if ((!value && value !== 0) || !Number.isInteger(value)) {
    return null;
  }

  let rub = (value / 100).toFixed(toFixed).split('.')[0];
  let penny = (value / 100).toFixed(toFixed).split('.')[1];

  return `${rub}${separator}${penny}`;
}

export function removeNodes(selectors: string[]) {
  selectors.forEach((selector) => {
    const node = document.querySelector(selector);

    if (node) node.remove();
  });
}

export function isEmptyObject(object: object) {
  return Object.keys(object).length !== 0 ? object : null;
}

export function isNotEmptyObjectCheck(object: object): Boolean {
  return Object.keys(object).length !== 0;
}

export function dateRevers(date: string, back?: boolean) {
  if (back) {
    return date.split('.').reverse().join('-');
  }
  return date.split('-').reverse().join('.');
}

export function dateWithoutTime(date: string) {
  if (date.includes('T')) return dateRevers(date.split('T')[0]);

  return date;
}

// сегодняшняя дата + n лет
export function dateToMax(year: number) {
  const now = new Date();
  now.setFullYear(now.getFullYear() + year);
  return now.toISOString();
}

export function formattingStorageFileIdsValue(
  value: string,
  backMode?: boolean
) {
  if (!value) {
    return;
  }
  if (backMode) {
    let arrValue = value.split('');
    let countSeparator = 0;
    arrValue.forEach((item, index) => {
      if (
        (index + 1) % 4 === 0 &&
        index + 1 + countSeparator < arrValue.length
      ) {
        arrValue.splice(index + 1 + countSeparator, 0, '-');
        countSeparator++;
      }
    });
    return arrValue.join('');
  }
  return value
    .split('')
    .filter((item) => item !== '-')
    .join('');
}

export function formattingStringOnlyNumber(data: string) {
  if (!data) return null;
  // @ts-ignore
  return data.match(/\d/g).join('');
}

// Переводим байты в килобайты и возвращаем строку
export function converterFileSize(size: string | number): string {
  if (size <= 0) {
    return '-';
  }
  return Math.round(+size / 1024 ** 2) > 0
    ? `${(+size / 1024 ** 2).toFixed(1)} мб`
    : `${Math.round(+size / 1024)} кб`;
}

export function makeFirstLetterUppercase(text: string) {
  if (!text) return;
  const arrText = text.trim().split('');
  return [...arrText[0].toUpperCase(), ...arrText.slice(1)].join('');
}

function isStringError(error: ApiError): error is string {
  return typeof error === 'string';
}

export function getResponseErrorText(error: any) {
  if (isStringError(error.response.data)) {
    return error.response.data;
  }

  if (error.response.data.errorString) {
    return error.response.data.errorString;
  }

  if (error.response.data.message) {
    return error.response.data.message;
  }

  return error.response.statusText;
}

export function errorHandler(error: any, isXml?: boolean) {
  const errorAlert = isXml
    ? ErrorAlertModule.setErrorAlertXml
    : ErrorAlertModule.setErrorAlert;

  if (error.response) {
    errorAlert(getResponseErrorText(error));
    return;
  }

  if (typeof error === 'string') {
    errorAlert(error);
    return;
  }

  if (!error) {
    errorAlert(ErrorMessage.DEFAULT);
  }
}

export const getTrusteeLabel = (role: string) => {
  switch (role) {
    case Role.PERSON:
      return 'Сведения о представителе физического лица';
    case Role.ORGANIZATION:
      return 'Сведения о представителе юридического лица';
    case Role.FOREIGN_ORGANIZATION:
      return 'Сведения о представителе иностранного юридического лица';
    default:
      throw new Error('Пользователь не найден');
  }
};

export const getTrusteeFieldGroup = (role: string) => {
  switch (role) {
    case Role.PERSON:
      return FieldGroup.PERSON;
    case Role.ORGANIZATION:
      return FieldGroup.ORGANIZATION;
    case Role.FOREIGN_ORGANIZATION:
      return FieldGroup.FOREIGN_ORGANIZATION;
    default:
      throw new Error('Заявитель не найден');
  }
};

export const addDocumentsWithEdit = (addStatement: AddStatementDraftResp) => {
  const isNoticeRecovery =
    addStatement.originalStatementType === StatementType.NOTICE_RECOVERY;
  const isAddDocuments =
    addStatement.addStatementType === AdditionalDocumentsType.ADD_DOCUMENTS;

  return isNoticeRecovery && isAddDocuments;
};

export const addDocumentsStepsCount = (withEdit: boolean) => (withEdit ? 3 : 2);

export const getAddStatementBreadcrumbs = (
  statementId: string
): IBreadCrumbs => ({
  back: true,
  path: [
    {
      text: RouteText.HOME,
      disabled: false,
      href: RoutePath.HOME,
    },
    {
      text: RouteText.STATEMENTS,
      disabled: false,
      href: RoutePath.STATEMENTS,
    },
    {
      text: RouteText.STATEMENT_DETAIL,
      disabled: false,
      href: `${RoutePath.STATEMENTS}/${statementId}`,
    },
  ],
});

export const getAddDocumentsBreadcrumbs = (statementId: string) => {
  const addStatementBreadcrumbs = getAddStatementBreadcrumbs(statementId);

  addStatementBreadcrumbs.path.push({
    text: RouteText.ADD_DOCUMENTS,
    disabled: true,
  });

  return addStatementBreadcrumbs;
};

export const getResendToFsspBreadcrumbs = (statementId: string) => {
  const addStatementBreadcrumbs = getAddStatementBreadcrumbs(statementId);

  addStatementBreadcrumbs.path.push({
    text: RouteText.ALTERNATIVE_SERVICE_RECEND,
    disabled: true,
  });

  return addStatementBreadcrumbs;
};

export const genderMap: Record<string, string> = {
  M: 'Мужской',
  F: 'Женский',
};

export const getSendLogParams = () => ({
  statementId: router.currentRoute.params.statementId,
  addStatementId: router.currentRoute.params.altStatementId,
});

type FormatterType<A> = (value: A) => string | number | Date;

/**
 * @param value Значение, которое нужно проверить на существование
 * @param formatter Функция, которую нужно вызвать, если значение есть
 * @param undefinedText Текст который вернет функция, если значения нет
 * @returns Если значение существует(value), вернет значение вызванной функции(formatter), иначе вернет переданный текст(undefinedText)
 */
export const formatExistValue = <
  A,
  B extends FormatterType<A>,
  C extends string
>(
  value: A | undefined | null,
  formatter: B,
  undefinedText: C
): ReturnType<B> | C => {
  if (value === undefined || value === null) {
    return undefinedText;
  }

  return formatter(value) as ReturnType<B>;
};

export const toUpperCaseFormatter = (str: string) => str.toUpperCase();

export const checkIsNewNotaryTariffName = (extId?: string) => {
  if (!extId) return false;

  const extIdDate = extId.split('-')[1];

  return extIdDate >= notaryTariffDate;
};

const SubjectText = {
  APPLICANT_PERSON: 'Заявитель',
  TRUSTEE:
    'Лицо действующее от имени юридического лица без доверенности/Представитель заявителя по доверенности в бумажном виде',
  ASSIGNEE: 'Лицо действующее от имени юридического лица без доверенности',
} as const;

const getAssigneeTitle = ({ role }: { role: RoleValues }) => {
  if (role === Role.PERSON) {
    return 'Законный представитель физического лица (без доверенности)';
  }

  return SubjectText.ASSIGNEE;
};

export const getSubjectTitle = ({
  statementType,
  subjectType,
  role,
}: {
  statementType: StatementType;
  role: RoleValues;
  subjectType: SubjectTypeValues;
}) => {
  if (statementType === StatementType.ROD) {
    if (role === Role.PERSON) {
      return SubjectText.APPLICANT_PERSON;
    }

    return SubjectText.ASSIGNEE;
  }

  if (statementType === StatementType.EXTRACTION_ROD) {
    if (role === Role.PERSON) {
      return SubjectText.APPLICANT_PERSON;
    }

    return SubjectText.TRUSTEE;
  }

  if (statementType === StatementType.EGRN_CONFIRM) {
    if (subjectType === SubjectType.ASSIGNEE) return getAssigneeTitle({ role });

    if (role === Role.PERSON) {
      return 'Заявитель или Представитель заявителя по нотариально заверенной доверенности в бумажном виде';
    }

    return 'Представитель заявителя по доверенности в бумажном виде';
  }

  if (subjectType === SubjectType.ASSIGNEE) getAssigneeTitle({ role });

  if (role === Role.PERSON) {
    return SubjectText.APPLICANT_PERSON;
  }

  return SubjectText.TRUSTEE;
};

export const getObligationType = (fieldName: string) => {
  return OBLIGATION_TYPES.filter((item) => item.value === fieldName)?.[0] || {}
}


export function compareFullName(user: {surname?: string, name?: string, patronymic?: string}, fullNameString: string) {
  // Собираем все непустые поля и разбиваем каждое на массив слов (в нижнем регистре)
  const fields: string[][] = [];
  for (const [key, value] of Object.entries(user)) {
    if (value)
      fields.push(value.trim().split(/\s+/).map(w => w.toLowerCase()));
  }

  // Разбиваем входную строку на слова (нижний регистр)
  const words: string[] = fullNameString.trim().split(/\s+/).map(w => w.toLowerCase());

  // Проверяем общее количество слов
  if (fields.reduce((sum, arr) => sum + arr.length, 0) !== words.length) return false;

  const n = fields.length;          // количество полей (1..3)
  const used: boolean[] = Array(n).fill(false); // какие поля уже использованы

  // Рекурсивная функция поиска
  function backtrack(pos: number): boolean {
    // Если дошли до конца строки, проверяем, все ли поля использованы
    if (pos === words.length) return used.every(v => v);

    // Пробуем поставить на текущую позицию любое ещё не использованное поле
    for (let i = 0; i < n; i++) {
      if (used[i]) continue;

      const fieldWords = fields[i];
      // Если поле не помещается в оставшуюся часть строки, пропускаем
      if (pos + fieldWords.length > words.length) continue;

      // Проверяем совпадение слов
      let match = true;
      for (let j = 0; j < fieldWords.length; j++) {
        if (words[pos + j] !== fieldWords[j]) {
          match = false;
          break;
        }
      }

      if (match) {
        used[i] = true;                          // помечаем поле как использованное
        if (backtrack(pos + fieldWords.length)) {
          return true;                           // если нашли решение, выходим
        }
        used[i] = false;                         // иначе откатываем
      }
    }
    return false; // ни одно поле не подошло
  }

  return backtrack(0);
}
